import React, { useState, useEffect, useRef, useCallback } from "react";
import "./TaskList.css";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { taskService } from "../services/taskService";
import { getPaginationTotalCount } from "../../../shared/utils/paginationHelper";
import profileIcon from "../../../shared/assets/icons/profile.png";
import bellIcon from "../../../shared/assets/icons/bell.png";
import backIcon from "../../../shared/assets/icons/back.png";
import filterIcon from "../../../shared/assets/icons/filter.png";
import addIcon from "../../../shared/assets/icons/add.png";
import actionIcon from "../../../shared/assets/icons/eye.png";
import filter from "../../../shared/assets/icons/filterIcon.png";
import FilterModal from "../../../components/FilterModal";
import SearchInput from "../../../components/SearchInput";
import DataTableGrid from "../../../shared/components/DataTableGrid";
import { formatDateString } from "../../../shared/utils/dateFormatter";
import { useTaskListContext } from "../../../contexts/TaskListContext";
import HeaderNav from "../../../shared/components/HeaderNav";
import { RiSearchEyeLine } from "react-icons/ri";
import { LuFilter } from "react-icons/lu";
import MainLoader from "../../../shared/components/MainLoader";
import { IoMdArrowBack } from "react-icons/io";
import { IoArrowUndoOutline } from "react-icons/io5";
import MobileFooterNav from "../../../shared/components/MobileFooterNav";

const TaskList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get("status");
  const ongoingFilter = searchParams.get("ongoing");
  const equipmentIdFilter = searchParams.get("equipment_id");
  const [tasks, setTasks] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState("-id");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    selectedStatus: [],
    selectedServiceCategories: [],
    selectedPriorities: [],
    selectedClients: [],
    selectedEmployees: [],
    reportedFromDate: "",
    reportedToDate: "",
    scheduledFromDate: "",
    scheduledToDate: "",
  });
  const tasksPerPage = 10;
  const [isMobile, setIsMobile] = useState(false);
  const priorityList = [
    { label: "LOW", value: 0 },
    { label: "MEDIUM", value: 1 },
    { label: "NORMAL", value: 2 },
    { label: "HIGH", value: 3 },
    { label: "URGENT", value: 4 },
  ];
  const [selectedTab, setSelectedTab] = useState("Ongoing");
  const [mobileTasks, setMobileTasks] = useState([]);
  const [mobileLoading, setMobileLoading] = useState(false);
  const [mobilePage, setMobilePage] = useState(1);
  const [hasMoreMobile, setHasMoreMobile] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const scrollContainerRef = useRef(null);
  const {
    taskListData,
    isLoading: loading,
    fetchTaskList,
  } = useTaskListContext();

  // Listen for Firebase notification events
  useEffect(() => {
    const handleRefresh = () => {
      fetchTaskList();
    };

    window.addEventListener("refreshTaskList", handleRefresh);
    return () => window.removeEventListener("refreshTaskList", handleRefresh);
  }, [fetchTaskList]);

  // Fetch mobile tasks when mobile mode is enabled
  useEffect(() => {
    if (isMobile) {
      setMobileTasks([]);
      setMobilePage(1);
      setHasMoreMobile(true);
      fetchMobileTasks(1, true);
    }
  }, [isMobile]);

  useEffect(() => {
    const fetchTasks = async () => {
      setError(null);
      try {
        let selectedStatusArray = [];
        if (ongoingFilter === "true" && !isMobile) {
          selectedStatusArray = [
            16, 20, 21, 22, 23, 24, 25, 26, 27, 28, 30, 32,
          ];
        } else if (statusFilter && !isMobile) {
          selectedStatusArray = statusFilter.split(",").map(Number);
        }

        const combinedSelectedStatus = [
          ...selectedStatusArray,
          ...(activeFilters.selectedStatus || []),
        ];

        const filters = {
          sortField,
          searchQuery,
          ...activeFilters,
          statusFilter: statusFilter && !isMobile ? statusFilter : null,
          selectedStatus: combinedSelectedStatus,
          ...(equipmentIdFilter && { equipment_id: equipmentIdFilter }),
        };

        const response = await fetchTaskList(currentPage, filters);

        if (
          response &&
          response.success &&
          response.data &&
          Array.isArray(response.data)
        ) {
          const formattedTasks = response.data.map((item) => ({
            id: item.id,
            wo_no: item.id,
            req_id: item.org_task_code ?? "N/A",
            equipment: item.equipment_name || "N/A",
            customer: item.customer_name,
            company: item.company_name,
            phone: item.phone_number,
            email: item.email,
            address:
              `${item.address1 || ""} ${item.address2 || ""}`.trim() || "N/A",
            issues: item.issues ? JSON.parse(item.issues).join(", ") : "N/A",
            category: item.item_category || "N/A",
            make: item.make_name || "N/A",
            model: item.model_name || "N/A",
            reported_time: item.reported_time,
            scheduled_from: item.scheduled_from,
            priority: getPriorityText(item.priority),
            status: item.status,
            technician: item.employee_name || (item.first_name ? `${item.first_name} ${item.last_name || ""}`.trim() : "N/A"),
            age: item.age,
          }));
          setTasks(formattedTasks);

          // Use same logic as Equipment page for production compatibility
          let totalCount = 0;

          // Method 1: From response headers
          if (response.headers) {
            const headerCount = getPaginationTotalCount(response.headers);
            if (headerCount > 0) {
              totalCount = headerCount;
            }
          }

          // Method 2: From response properties (direct)
          if (totalCount === 0) {
            const responseCount =
              response.total_count ||
              response.total ||
              response.totalCount ||
              response.count;
            if (responseCount && responseCount > 0) {
              totalCount = parseInt(responseCount, 10);
            }
          }

          // Method 3: Calculate based on current data
          if (totalCount === 0) {
            if (currentPage === 1 && formattedTasks.length < tasksPerPage) {
              totalCount = formattedTasks.length;
            } else {
              totalCount =
                (currentPage - 1) * tasksPerPage + formattedTasks.length;
              if (formattedTasks.length === tasksPerPage) {
                totalCount += tasksPerPage;
              }
            }
          }

          // Fallback
          if (totalCount === 0) {
            totalCount = Math.max(formattedTasks.length, tasksPerPage);
          }

          setTotalItems(totalCount);
        } else {
          setTasks([]);
          setTotalItems(0);
        }
      } catch (error) {
        console.error("Error fetching tasks:", error);
        setError(error.message);
        setTasks([]);
        setTotalItems(0);
      }
    };
    fetchTasks();
  }, [
    currentPage,
    sortField,
    searchQuery,
    activeFilters,
    statusFilter,
    isMobile,
    fetchTaskList,
  ]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalItems / tasksPerPage));

  // Debug logging for production issues
  useEffect(() => {
    console.log("Pagination Debug:", {
      totalItems,
      totalPages,
      currentPage,
      tasksCount: tasks.length,
    });
  }, [totalItems, totalPages, currentPage, tasks.length]);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle between field and -field
      setSortField(sortField.startsWith("-") ? field : `-${field}`);
    } else {
      // First click, set field without -
      setSortField(field);
    }
    setCurrentPage(1);
  };

  const searchTimerRef = useRef(null);
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearchQuery(val);
      setCurrentPage(1);
    }, 500);
  };

  const handleApplyFilters = async (filters) => {
    setActiveFilters(filters);
    setCurrentPage(1); // Reset to first page when applying filters
  };

  const handleClearFilters = () => {
    setActiveFilters({
      selectedStatus: [],
      selectedServiceCategories: [],
      selectedPriorities: [],
      selectedClients: [],
      selectedEmployees: [],
      reportedFromDate: "",
      reportedToDate: "",
      scheduledFromDate: "",
      scheduledToDate: "",
    });
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (activeFilters.selectedStatus?.length > 0) count++;
    if (activeFilters.selectedServiceCategories?.length > 0) count++;
    if (activeFilters.selectedPriorities?.length > 0) count++;
    if (activeFilters.reportedFromDate || activeFilters.reportedToDate) count++;
    if (activeFilters.scheduledFromDate || activeFilters.scheduledToDate)
      count++;
    return count;
  };

  const getSortIcon = (field) => {
    if (sortField === field) return " ↑";
    if (sortField === `-${field}`) return " ↓";
    return "";
  };

  const getPriorityText = (priority) => {
    const priorityMap = {
      0: "Low",
      1: "Medium",
      2: "Normal",
      3: "High",
      4: "Urgent",
    };
    return priorityMap[priority] || "N/A";
  };

  const getMyCurrentStatus = (status) => {
    switch (status) {
      case 0:
        return "UNASSIGNED";
      case 3:
        return "REJECTED";
      case 10:
        return "OPEN";
      case 12:
        return "ASSIGNED";
      case 14:
        return "WORK ON HOLD";
      case 16:
        return "ACCEPTED";
      case 34:
        return "COMPLETED";
      case -12:
        return "UNASSIGNED";
      default:
        return "WIP";
    }
  };

  const fetchMobileTasks = async (page = mobilePage, isNewLoad = false) => {
    if (!isMobile) return;

    if (isNewLoad) {
      setMobileLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const filters = {
        sortField,
        searchQuery,
        ...activeFilters,
        ...(equipmentIdFilter && { equipment_id: equipmentIdFilter }),
      };

      const response = await taskService.fetchMaintenanceRequests(
        page,
        filters,
      );

      if (response.success && response.data && Array.isArray(response.data)) {
        const formattedTasks = response.data.map((item) => ({
          id: item.id,
          wo_no: item.id,
          req_id: item.org_task_code ?? "N/A",
          equipment: item.equipment_name || "N/A",
          customer: item.customer_name,
          company: item.company_name,
          phone: item.phone_number,
          email: item.email,
          address:
            `${item.address1 || ""} ${item.address2 || ""}`.trim() || "N/A",
          issues: item.issues ? JSON.parse(item.issues).join(", ") : "N/A",
          category: item.item_category || "N/A",
          make: item.make_name || "N/A",
          model: item.model_name || "N/A",
          reported_time: formatDateString(item.reported_time),
          scheduled_from: item.scheduled_from,
          priority: getPriorityText(item.priority),
          status: item.status,
          technician: item.employee_name || (item.first_name ? `${item.first_name} ${item.last_name || ""}`.trim() : "N/A"),
          age: item.age,
        }));

        if (isNewLoad) {
          setMobileTasks(formattedTasks);
        } else {
          setMobileTasks((prev) => [...prev, ...formattedTasks]);
        }

        setHasMoreMobile(formattedTasks.length === 10);
      } else {
        if (isNewLoad) {
          setMobileTasks([]);
        }
        setHasMoreMobile(false);
      }
    } catch (error) {
      console.error("Error fetching mobile tasks:", error);
      if (isNewLoad) {
        setMobileTasks([]);
      }
      setHasMoreMobile(false);
    } finally {
      setMobileLoading(false);
      setLoadingMore(false);
    }
  };

  const handleScroll = useCallback(
    (e) => {
      if (!isMobile || loadingMore || !hasMoreMobile) return;

      const { scrollTop, scrollHeight, clientHeight } = e.target;

      // Trigger when user scrolls to within 50px of bottom
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        const nextPage = mobilePage + 1;
        setMobilePage(nextPage);
        fetchMobileTasks(nextPage, false);
      }
    },
    [isMobile, loadingMore, hasMoreMobile, mobilePage],
  );

  const filteredTasks = tasks.filter((task) => {
    const statusMap = { 10: "Open", 20: "In Progress", 30: "Completed" };
    const statusText = statusMap[task.status] || "Unknown";
    return statusText === selectedTab;
  });

  return (
    <div className="task-list-container">
      {/* Main Header */}
      {/*Greeting Header */}
      <HeaderNav profileIcon={profileIcon} />

      {/* Mobile Header */}
      <header className="mobile-header">
        <div className="header-left" onClick={() => navigate('/dashboard')}>
          <IoArrowUndoOutline color="black" size={25} alt="Back" />
          <h1 className="app-title">My Requests</h1>
        </div>
        <h2 className="page-title">Maintenance List</h2>
        <div
          className="header-right"
          onClick={() => setIsFilterModalOpen(true)}>
          <LuFilter size={20} />
          <button
            className="mobile_task_add-button"
            onClick={() => navigate("/maintenance")}
          >
            <img src={addIcon} alt="Add" />
          </button>
        </div>
      </header>

      {/* Mobile Search Bar */}
      <div className="mobile-search-bar">
        <SearchInput
          value={searchInput}
          onChange={handleSearchChange}
          placeholder="Search Req Id"
          width="100%"
          inputType="number"
        />
      </div>

      {/* Filters Section */}
      <div className="header">
        <h2 className="page-title">Maintenance Request</h2>
        <div className="filters">
          <SearchInput
            value={searchInput}
            onChange={handleSearchChange}
            placeholder="Search Req Id"
            width="250px"
            inputType="number"
          />
          <button
            className="search-button filter-button-container"
            onClick={() => setIsFilterModalOpen(true)}
          >
            <LuFilter alt="Filter" size={16} color="white" />
            {/* <img src={filterIcon} alt="Filter" /> */}
            {getActiveFilterCount() > 0 && (
              <span className="filter-indicator">{getActiveFilterCount()}</span>
            )}
          </button>
          <button
            className="add-button"
            onClick={() => navigate("/maintenance")}
          >
            <img src={addIcon} alt="Add" />
          </button>
        </div>
      </div>

      {/* Desktop Table - Wrapped in scrollable container */}
      <div className="task-table-container">
        {loading ? (
          <MainLoader />
        ) : error ? (
          <div className="error-container">
            <div className="error-box">
              <div className="error-icon">📭</div>
              <div className="error-text">No Request Available</div>
            </div>
          </div>
        ) : tasks.length === 0 ? (
          <div className="error-container">
            <div className="error-box">
              <div className="error-icon">📭</div>
              <div className="error-text">No Request Available</div>
            </div>
          </div>
        ) : (
          <DataTableGrid
            items={tasks}
            loading={loading}
            error={error}
            emptyMessage="No tasks found"
            onSort={handleSort}
            onRowClick={(task) => navigate(`/maintenancedetails/${task.id}`)}
            sortField={sortField}
            getSortIcon={getSortIcon}
            columns={[
              { key: "req_id", title: "Req ID", sortKey: "org_task_code" },
              { key: "category", title: "Product", sortKey: "item_category" },
              {
                key: "equipment",
                title: "Equipment",
                sortKey: "equipment_name",
              },
              {
                key: "priority",
                title: "Priority",
                sortKey: "priority",
              },
              {
                key: "status",
                title: "Status",
                sortKey: "status",
                render: (task) => {
                  const statusText = getMyCurrentStatus(task.status);
                  return (
                    <>
                      <span
                        className={`status-circle ${statusText.toLowerCase().replace(/\s+/g, "-")}`}
                      ></span>
                      {statusText}
                    </>
                  );
                },
              },
              { key: "technician", title: "Technician", sortKey: "technician" },
              {
                key: "actions",
                title: "Actions",
                render: (task) => (
                  <button
                    className="action-button"
                    onClick={() => navigate(`/maintenancedetails/${task.id}`)}
                  >
                    <RiSearchEyeLine
                      src={actionIcon}
                      alt="action"
                      color="#35605a"
                    />
                    {/* <img src={actionIcon} alt="action" className="icon" /> */}
                  </button>
                ),
              },
            ]}
          />
        )}
      </div>

      {/* Desktop Pagination */}
      {!isMobile && (
        <div className="pagination">
          <button
            className="page-button"
            onClick={handlePreviousPage}
            disabled={currentPage === 1 || loading}
          >
            {"<"}
          </button>
          {Array.from({ length: Math.min(totalPages, 10) }, (_, index) => {
            let pageNumber;
            if (totalPages <= 10) {
              pageNumber = index + 1;
            } else {
              const start = Math.max(1, currentPage - 5);
              const end = Math.min(totalPages, start + 9);
              pageNumber = start + index;
              if (pageNumber > end) return null;
            }
            return (
              <button
                key={pageNumber}
                className={`page-number-button ${currentPage === pageNumber ? "active" : ""}`}
                onClick={() => handlePageClick(pageNumber)}
                disabled={loading}
              >
                {pageNumber}
              </button>
            );
          }).filter(Boolean)}
          <button
            className="page-button"
            onClick={handleNextPage}
            disabled={currentPage === totalPages || loading}
          >
            {">"}
          </button>
        </div>
      )}
      {isMobile && <div style={{ marginTop: 10 }}></div>}
      {/* Mobile View: Task Cards */}
      <div
        className="task-content-container"
        ref={scrollContainerRef}
        style={{
          height: isMobile ? "calc(110vh - 80px)" : "auto",
          overflowY: isMobile ? "scroll" : "visible",
          WebkitOverflowScrolling: "touch",
        }}
        onScroll={isMobile ? handleScroll : undefined}
      >
        {mobileLoading ? (
          <div className="loading-container">
            <div className="loading-text">Loading...</div>
          </div>
        ) : error ? (
          <div className="mobile-error-container">
            <div className="mobile-error-text">No Request Available</div>
          </div>
        ) : mobileTasks.length === 0 ? (
          <div className="mobile-no-task-container">No Request Available</div>
        ) : (
          <>
            {mobileTasks.map((task) => (
              <div
                key={task.id}
                className="task-card-new"
                onClick={() => navigate(`/maintenancedetails/${task.id}`)}
              >
                {/* Top Meta Row */}
                <div className="task-top-row">
                  <span className="task-date">{task.reported_time}</span>

                  <span className={`task-status status-${task.status}`}>
                    {getMyCurrentStatus(task.status)}
                  </span>
                </div>

                {/* Equipment Row */}
                <div className="task-main-row">
                  <div className="task-image">
                    {/* <img src={equipmentPlaceholder} alt="Equipment" /> */}
                    <img
                      src="https://cdn-icons-png.flaticon.com/512/2933/2933245.png"
                      alt="Equipment"
                      className="task-equipment-img"
                    />
                  </div>

                  <div className="task-details">
                    <h3 className="task-equipment">{task.equipment}</h3>
                    <p className="task-company">
                      {task.category} | {task.company}
                    </p>
                    <p className="task-warranty">{task.req_id}</p>
                  </div>
                </div>

                {/* Issue Section */}
                <div className="task-issue-row">
                  <p className="task-issue-text">{task.issues}</p>
                  <span className="task-more">More ▾</span>
                </div>
              </div>
              // <div key={task.id} className="task-card" onClick={() => navigate(`/maintenancedetails/${task.id}`)}>
              //   {/* Header Section */}
              //   <div className="card-header">
              //     <span className="technician-name">{task.technician}</span>
              //     <span className="wo-number">{task.req_id}</span>
              //   </div>

              //   {/* Body Section */}
              //   <div className="card-body">
              //     <div className="priority-date-row">
              //       <span className={`priority-badge`}>
              //         {task.priority}
              //       </span>
              //       <span className="schedule-date">{task.reported_time}</span>
              //     </div>

              //     <div className="customer-info">
              //       <p><strong>Customer:</strong> {task.customer}</p>
              //       <p><strong>Company:</strong> {task.company}</p>
              //       <p><strong>Equipment:</strong> {task.equipment}</p>
              //       <p><strong>Product:</strong> {task.category}</p>
              //     </div>

              //     <div className="progress-bar-container">
              //       <div className="status-label">
              //         <span>{getMyCurrentStatus(task.status)}</span>
              //       </div>
              //       <div className="progress-bar">
              //         <div className={`progress-step ${task.status >= 10 ? 'completed' : ''}`}></div>
              //         <div className={`progress-step ${task.status >= 20 ? 'completed' : task.status === 20 ? 'active' : ''}`}></div>
              //         <div className={`progress-step ${task.status >= 30 ? 'completed' : task.status === 30 ? 'active' : ''}`}></div>
              //       </div>
              //     </div>

              //     <p className="equipment-description">
              //       <strong>Issues:</strong> {task.issues}
              //     </p>
              //   </div>

              //   {/* Action Buttons */}
              //   <div className="card-actions">
              //     <button className="action-button" onClick={(e) => e.stopPropagation()}>
              //       <img src={actionIcon} alt="Action" />
              //     </button>
              //     <button className="action-button" onClick={(e) => { e.stopPropagation(); setIsFilterModalOpen(true); }}>
              //       <img src={filterIcon} alt="Filter" />
              //     </button>
              //     <button className="action-button" onClick={(e) => { e.stopPropagation(); navigate('/maintenance'); }}>
              //       <img src={addIcon} alt="Add" />
              //     </button>
              //   </div>
              // </div>
            ))}
            {loadingMore && <MainLoader />}
          </>
        )}
      </div>

      {/* Filter Modal */}
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        initialFilters={activeFilters}
      />
      <MobileFooterNav />
    </div>
  );
};

export default TaskList;
